import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import ModalComponent from '../../components/Modal/Modal'
import { sendPaymentLink, getSelectablePayments } from '../../features/paymentLink/paymentLink'
import { selectFeatureFlag } from '../../features/featureFlags/selectors'
import { REQUEST_ERRORS_SMS } from '../../constants/requestErrorFlags'

const MODES = [
  { value: 'missed', label: 'Missed Payment', hint: 'Selects the client\'s failed and rescheduled payments' },
  { value: 'full', label: 'Full Balance', hint: 'Selects every outstanding payment' },
  { value: 'custom', label: 'Custom Amount', hint: 'Pick payments and enter an amount up to their total' },
]

const round2 = (value) => Math.round(Number(value) * 100) / 100

const formatMoney = (value) => `$${round2(value || 0).toFixed(2)}`

const formatDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
}

const presetSelection = (mode, selectable) => {
  const map = {}
  selectable.forEach((payment) => {
    // A rescheduled declined payment is offered as its retry (attempt 2+), still a missed payment.
    if (mode === 'full' || (mode === 'missed' && (payment.status === 'failed' || payment.attempt > 1))) {
      map[payment._id] = true
    }
  })
  return map
}

const SendPaymentLinkModal = ({ userId, open, onClose }) => {
  const dispatch = useDispatch()
  const { selectable, selectableLoading } = useSelector((state) => state.paymentLink)
  const smsStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_SMS))

  const [mode, setMode] = useState('missed')
  const [amount, setAmount] = useState('')
  const [channels, setChannels] = useState({ email: true, sms: false })
  const [selected, setSelected] = useState({})
  const [sending, setSending] = useState(false)
  const [closeSignal, setCloseSignal] = useState(false)

  useEffect(() => {
    if (open) {
      setMode('missed')
      setAmount('')
      setChannels({ email: true, sms: false })
      setSelected({})
      setSending(false)
      dispatch(getSelectablePayments({ userId }))
    }
  }, [open, userId, dispatch])

  useEffect(() => {
    if (open && mode !== 'custom') {
      setSelected(presetSelection(mode, selectable))
    }
  }, [open, mode, selectable])

  const selectedIds = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected])

  const maxAmount = useMemo(() => round2(
    selectable
      .filter((payment) => selected[payment._id])
      .reduce((sum, payment) => sum + (payment.outstanding || 0), 0)
  ), [selectable, selected])

  const toggleChannel = (channel) => {
    setChannels((prev) => ({ ...prev, [channel]: !prev[channel] }))
  }

  const togglePayment = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleSend = async () => {
    const selectedChannels = Object.keys(channels).filter((key) => channels[key])
    if (!selectedChannels.length) {
      toast.error('Select at least one delivery channel')
      return
    }
    if (!selectedIds.length) {
      toast.error('Select at least one payment to collect')
      return
    }
    if (mode === 'custom') {
      const value = Number(amount)
      if (!value || value <= 0) {
        toast.error('Enter a valid amount')
        return
      }
      if (round2(value) > maxAmount + 0.001) {
        toast.error('The amount cannot exceed the selected payments')
        return
      }
    }

    setSending(true)
    try {
      const result = await dispatch(sendPaymentLink({
        userId,
        mode,
        amount: mode === 'custom' ? Number(amount) : undefined,
        channels: selectedChannels,
        selectedPaymentIds: selectedIds,
      })).unwrap()
      toast.success('Payment link sent')
      if (smsStrict && result?.smsError) {
        toast.warn(`The text message was not sent: ${result.smsError}`)
      }
      setCloseSignal((prev) => !prev)
    } catch (error) {
      const message = typeof error === 'string' ? error : (error?.message || 'Failed to send payment link')
      toast.error(message)
    } finally {
      setSending(false)
    }
  }

  return (
    <ModalComponent
      title="Send Payment Link"
      customOpenModal={open}
      disableBtn={false}
      onClose={onClose}
      modalButtonTitle="Send"
      functionButton={handleSend}
      disableCloseFunctionButton={true}
      triggerCloseModal={closeSignal}
      disabled={sending}
      modalWidth="min-w-[520px]"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {MODES.map((item) => (
            <label key={item.value} className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="paylink-mode"
                value={item.value}
                checked={mode === item.value}
                onChange={() => setMode(item.value)}
                className="mt-1"
              />
              <span className="flex flex-col">
                <span className="text-text-primary text-sm font-bold">{item.label}</span>
                <span className="text-text-secondary text-xs">{item.hint}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-text-primary text-sm font-bold">Payments to collect</span>
          {selectableLoading && <span className="text-text-secondary text-xs">Loading…</span>}
          {!selectableLoading && selectable.length === 0 && (
            <span className="text-text-secondary text-xs">No outstanding payments to collect</span>
          )}
          {!selectableLoading && selectable.length > 0 && (
            <div className="flex flex-col gap-1 max-h-48 overflow-y-auto border border-divider rounded-lg p-2">
              {selectable.map((payment) => (
                <label key={payment._id} className="flex items-center justify-between gap-2 cursor-pointer text-sm">
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!selected[payment._id]}
                      onChange={() => togglePayment(payment._id)}
                    />
                    <span className="text-text-primary">{formatDate(payment.dueDate)}</span>
                    <span className="text-text-secondary text-xs capitalize">{payment.status}</span>
                  </span>
                  <span className="text-text-primary">{formatMoney(payment.outstanding)}</span>
                </label>
              ))}
            </div>
          )}
          <span className="text-text-secondary text-xs">Selected total: {formatMoney(maxAmount)}</span>
        </div>

        {mode === 'custom' && (
          <input
            type="number"
            min="0"
            step="0.01"
            max={maxAmount}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Amount (max ${formatMoney(maxAmount)})`}
            className="border border-divider rounded-lg px-3 py-2 text-sm"
          />
        )}

        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-text-primary">
            <input type="checkbox" checked={channels.email} onChange={() => toggleChannel('email')} />
            Email
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-text-primary">
            <input type="checkbox" checked={channels.sms} onChange={() => toggleChannel('sms')} />
            SMS
          </label>
        </div>
      </div>
    </ModalComponent>
  )
}

export default SendPaymentLinkModal
